import React, { useState, useEffect, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { db } from '../../../firebase/config';
import { collection, query, where, getDocs, orderBy, limit, Timestamp } from 'firebase/firestore';
import { CashRegisterState, CashMovement, CashMovementType } from 'common/types';
import styles from './cashMovementsAdmin.module.css';
import { appContext } from 'common/contexts/appContext';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { printComponent } from 'react-print-tool';
import { SearchTwoTone } from 'common/icons';

// Definir el tipo para las opciones de mes
type MonthOption = {
  value: string;
  label: string;
  startDate: number;
  endDate: number;
};

const CashMovementsAdmin: React.FC = () => {
  const [t] = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [cashRegisters, setCashRegisters] = useState<CashRegisterState[]>([]);
  const [selectedRegister, setSelectedRegister] = useState<string | null>(null);
  const [cashMovements, setCashMovements] = useState<CashMovement[]>([]);
  const [filteredMovements, setFilteredMovements] = useState<CashMovement[]>([]);
  const [stats, setStats] = useState<{
    initialAmount: number;
    totalAdditions: number;
    totalWithdrawals: number;
    finalAmount: number;
  }>({ initialAmount: 0, totalAdditions: 0, totalWithdrawals: 0, finalAmount: 0 });
  
  // Referencia para la impresión
  const printRef = useRef<HTMLDivElement>(null);
  
  // Estado para el filtro de mes
  const [monthOptions, setMonthOptions] = useState<MonthOption[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<MonthOption | null>(null);

  const { state } = useContext(appContext);

  // Generar opciones de mes al montar el componente
  useEffect(() => {
    const generateMonthOptions = () => {
      const options: MonthOption[] = [];
      const currentDate = new Date();
      
      // Generar opciones para los últimos 12 meses
      for (let i = 0; i < 12; i++) {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
        const monthName = format(date, 'MMMM yyyy', { locale: es });
        const capitalizedMonthName = monthName.charAt(0).toUpperCase() + monthName.slice(1);
        
        const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1).getTime();
        const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999).getTime();
        
        options.push({
          value: `${date.getFullYear()}-${date.getMonth() + 1}`,
          label: capitalizedMonthName,
          startDate: startOfMonth,
          endDate: endOfMonth
        });
      }
      
      setMonthOptions(options);
      
      // Seleccionar el mes actual por defecto
      if (options.length > 0) {
        setSelectedMonth(options[0]);
      }
    };
    
    generateMonthOptions();
  }, []);
  
  // Fetch cash registers on component mount
  useEffect(() => {
    //fetchCashRegisters();
    if (state.cashRegisters.length > 0 && !selectedRegister) {
      setSelectedRegister(state.cashRegisters[0].id);
    }
  }, []);

  // Fetch cash movements when a register is selected
  useEffect(() => {
    if (selectedRegister) {
      fetchCashMovements(selectedRegister);
    } else {
      setCashMovements([]);
      setFilteredMovements([]);
      setStats({ initialAmount: 0, totalAdditions: 0, totalWithdrawals: 0, finalAmount: 0 });
    }
  }, [selectedRegister]);
  
  // Filtrar movimientos por mes cuando cambia el mes seleccionado o los movimientos
  useEffect(() => {
    if (selectedMonth && cashMovements.length > 0) {
      const filtered = cashMovements.filter(movement => {
        return movement.date >= selectedMonth.startDate && movement.date <= selectedMonth.endDate;
      });
      
      setFilteredMovements(filtered);
      
      // Recalcular estadísticas basadas en los movimientos filtrados
      const initialMovement = filtered.find(m => m.type === CashMovementType.INITIAL);
      const initialAmount = initialMovement ? initialMovement.amount : 0;
      
      const additions = filtered
        .filter(m => m.type === CashMovementType.ADDITION)
        .reduce((sum, m) => sum + m.amount, 0);
      
      const withdrawals = filtered
        .filter(m => m.type === CashMovementType.WITHDRAWAL)
        .reduce((sum, m) => sum + m.amount, 0);
      
      setStats({
        initialAmount,
        totalAdditions: additions,
        totalWithdrawals: withdrawals,
        finalAmount: initialAmount + additions - withdrawals
      });
    } else {
      setFilteredMovements(cashMovements);
    }
  }, [selectedMonth, cashMovements]);

  // Fetch cash registers (closed and open)
  const fetchCashRegisters = async () => {
    setLoading(true);
    try {
      const cashRegisterCollection = collection(db, 'cashRegister');
      const q = query(
        cashRegisterCollection,
        where('parentUserId', '==', state.parentId),
        orderBy('openDate', 'desc'),
        limit(20) // Limit to last 20 registers
      );

      const querySnapshot = await getDocs(q);
      const registers: CashRegisterState[] = [];
      
      querySnapshot.forEach((doc) => {
        const registerData = { id: doc.id, ...doc.data() } as CashRegisterState;
        registers.push(registerData);
      });

      setCashRegisters(registers);
      
      // Select the most recent register by default
      if (registers.length > 0 && !selectedRegister) {
        setSelectedRegister(registers[0].id);
      }
    } catch (err) {
      console.error('Error fetching cash registers:', err);
      setError(t('admin.cashMovements.errorFetching', 'Error al obtener registros de caja'));
    } finally {
      setLoading(false);
    }
  };

  // Fetch cash movements for a specific register
  const fetchCashMovements = async (registerId: string) => {
    setLoading(true);
    try {
      const selectedRegisterData = state.cashRegisters.find(register => register.id === registerId);
      
      if (!selectedRegisterData) {
        throw new Error('Register not found');
      }

      const cashMovementsCollection = collection(db, 'cashMovements');
      let movementsQuery;
      
      if (selectedRegisterData.closeDate) {
        // If register is closed, get movements between open and close dates
        movementsQuery = query(
          cashMovementsCollection,
          where('parentUserId', '==', state.parentId),
          where('date', '>=', selectedRegisterData.openDate),
          where('date', '<=', selectedRegisterData.closeDate),
          orderBy('date', 'asc')
        );
      } else {
        // If register is still open, get all movements since open date
        movementsQuery = query(
          cashMovementsCollection,
          where('parentUserId', '==', state.parentId),
          where('date', '>=', selectedRegisterData.openDate),
          orderBy('date', 'asc')
        );
      }

      const movementsSnapshot = await getDocs(movementsQuery);
      
      const movements: CashMovement[] = [];
      movementsSnapshot.forEach((doc) => {
        const movementData = { id: doc.id, ...doc.data() } as CashMovement;
        movements.push(movementData);
      });

      setCashMovements(movements);
      
      // Calculate statistics
      const initialMovement = movements.find(m => m.type === CashMovementType.INITIAL);
      const initialAmount = initialMovement ? initialMovement.amount : 0;
      
      const additions = movements
        .filter(m => m.type === CashMovementType.ADDITION)
        .reduce((sum, m) => sum + m.amount, 0);
      
      const withdrawals = movements
        .filter(m => m.type === CashMovementType.WITHDRAWAL)
        .reduce((sum, m) => sum + m.amount, 0);
      
      setStats({
        initialAmount,
        totalAdditions: additions,
        totalWithdrawals: withdrawals,
        finalAmount: initialAmount + additions - withdrawals
      });
    } catch (err) {
      console.error('Error fetching cash movements:', err);
      setError(t('admin.cashMovements.errorFetchingMovements', 'Error al obtener movimientos de caja'));
    } finally {
      setLoading(false);
    }
  };

  // Función para manejar el cambio de mes
  const handleMonthChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value;
    const selectedOption = monthOptions.find(option => option.value === selectedValue) || null;
    setSelectedMonth(selectedOption);
  };
  
  // Función para imprimir los movimientos
  const handlePrint = () => {
    if (printRef.current) {
      // Crear el componente a imprimir
      const PrintableContent = () => (
        <div style={{ padding: '20px' }}>
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <h1>{t('admin.cashMovements.printTitle', 'Informe de Movimientos de Caja')}</h1>
            {selectedMonth && <p>{selectedMonth.label}</p>}
          </div>
          
          {/* Tabla personalizada para imprimir */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px' }}>
            <thead>
              <tr>
                <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', backgroundColor: '#f2f2f2' }}>
                  {t('admin.cashMovements.date', 'Fecha')}
                </th>
                <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', backgroundColor: '#f2f2f2' }}>
                  {t('admin.cashMovements.type', 'Tipo')}
                </th>
                <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', backgroundColor: '#f2f2f2' }}>
                  {t('admin.cashMovements.notes', 'Notas')}
                </th>
                <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', backgroundColor: '#f2f2f2' }}>
                  {t('admin.cashMovements.amount', 'Monto')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredMovements.length > 0 ? (
                filteredMovements.map((movement) => (
                  <tr key={movement.id}>
                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>
                      {formatDate(movement.date)}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>
                      {getMovementTypeText(movement.type)}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>
                      {movement.notes || '-'}
                    </td>
                    <td style={{ 
                      border: '1px solid #ddd', 
                      padding: '8px', 
                      textAlign: 'left',
                      color: movement.type === CashMovementType.WITHDRAWAL ? 'red' : 
                             (movement.type === CashMovementType.ADDITION || movement.type === CashMovementType.INITIAL) ? 'green' : 'inherit'
                    }}>
                      {movement.type === CashMovementType.WITHDRAWAL ? '-' : '+'}
                      {formatCurrency(movement.amount)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} style={{ border: '1px solid #ddd', padding: '20px', textAlign: 'center' }}>
                    {t('admin.cashMovements.noMovements', 'No hay movimientos registrados para el período seleccionado')}
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3} style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>
                  {t('admin.cashMovements.initialAmount', 'Monto Inicial')}:
                </td>
                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', fontWeight: 'bold' }}>
                  {formatCurrency(stats.initialAmount)}
                </td>
              </tr>
              <tr>
                <td colSpan={3} style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>
                  {t('admin.cashMovements.additions', 'Adiciones')}:
                </td>
                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', fontWeight: 'bold', color: 'green' }}>
                  +{formatCurrency(stats.totalAdditions)}
                </td>
              </tr>
              <tr>
                <td colSpan={3} style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>
                  {t('admin.cashMovements.withdrawals', 'Retiros')}:
                </td>
                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', fontWeight: 'bold', color: 'red' }}>
                  -{formatCurrency(stats.totalWithdrawals)}
                </td>
              </tr>
              <tr>
                <td colSpan={3} style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>
                  {t('admin.cashMovements.finalAmount', 'Monto Final')}:
                </td>
                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', fontWeight: 'bold' }}>
                  {formatCurrency(stats.finalAmount)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      );
      
      // Utilizar printComponent para imprimir
      printComponent(<PrintableContent />);
    }
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return format(date, 'dd/MM/yyyy HH:mm');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
    }).format(amount);
  };

  const getMovementTypeText = (type: CashMovementType) => {
    switch (type) {
      case CashMovementType.INITIAL:
        return t('terminal.cashRegister.initialCash', 'Monto Inicial');
      case CashMovementType.ADDITION:
        return t('terminal.cashRegister.addition', 'Adición de Efectivo');
      case CashMovementType.WITHDRAWAL:
        return t('terminal.cashRegister.withdrawal', 'Retiro de Efectivo');
      case CashMovementType.CLOSING:
        return t('terminal.cashRegister.closing', 'Cierre de Caja');
      default:
        return type;
    }
  };

  const getStatusBadgeClass = (isOpen: boolean) => {
    return isOpen ? styles.openBadge : styles.closedBadge;
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t('admin.cashMovements.title', 'Movimientos de Caja')}</h1>
        
        <div className={styles.actions}>
          {/* Filtro por mes */}
          <div className={styles.filterContainer}>
            <SearchTwoTone className={styles.filterIcon} />
            <select
              className={styles.monthFilter}
              value={selectedMonth?.value || ''}
              onChange={handleMonthChange}
            >
              {monthOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Botón de imprimir */}
          <button className="btn btn-primary" onClick={handlePrint}>
            {t('admin.cashMovements.print', 'Imprimir')}
          </button>
        </div>
      </div>
      
      {error && <div className={styles.error}>{error}</div>}
      
      <div className={styles.content}>
        <div className={styles.sidebar}>
          <h2 className={styles.sidebarTitle}>{t('admin.cashMovements.registers', 'Registros de Caja')}</h2>
          
          {loading &&   state.cashRegisters.length === 0 ? (
            <div className={styles.loading}>{t('common.loading', 'Cargando...')}</div>
          ) : (
            <div className={styles.registersList}>
              {state.cashRegisters.map((register) => (
                <div 
                  key={register.id} 
                  className={`${styles.registerItem} ${selectedRegister === register.id ? styles.selected : ''}`}
                  onClick={() => setSelectedRegister(register.id)}
                >
                  <div className={styles.registerHeader}>
                    <span className={styles.registerDate}>{formatDate(register.openDate)}</span>
                    <span className={getStatusBadgeClass(register.isOpen)}>
                      {register.isOpen 
                        ? t('admin.cashMovements.open', 'Abierta')
                        : t('admin.cashMovements.closed', 'Cerrada')
                      }
                    </span>
                  </div>
                  <div className={styles.registerAmount}>
                    {t('admin.cashMovements.initialAmount', 'Monto Inicial')}: {formatCurrency(register.initialAmount)}
                  </div>
                  {!register.isOpen && register.closeDate && (
                    <div className={styles.registerClosedDate}>
                      {t('admin.cashMovements.closedAt', 'Cerrada el')}: {formatDate(register.closeDate)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className={styles.mainContent}>
          {selectedRegister ? (
            <>
              <div className={styles.statsContainer}>
                <div className={styles.statCard}>
                  <div className={styles.statLabel}>{t('admin.cashMovements.initialAmount', 'Monto Inicial')}</div>
                  <div className={styles.statValue}>{formatCurrency(stats.initialAmount)}</div>
                </div>
                
                <div className={styles.statCard}>
                  <div className={styles.statLabel}>{t('admin.cashMovements.additions', 'Adiciones')}</div>
                  <div className={`${styles.statValue} ${styles.addition}`}>{formatCurrency(stats.totalAdditions)}</div>
                </div>
                
                <div className={styles.statCard}>
                  <div className={styles.statLabel}>{t('admin.cashMovements.withdrawals', 'Retiros')}</div>
                  <div className={`${styles.statValue} ${styles.withdrawal}`}>{formatCurrency(stats.totalWithdrawals)}</div>
                </div>
                
                <div className={styles.statCard}>
                  <div className={styles.statLabel}>{t('admin.cashMovements.finalAmount', 'Monto Final')}</div>
                  <div className={styles.statValue}>{formatCurrency(stats.finalAmount)}</div>
                </div>
              </div>
              
              <div className={styles.movementsContainer}>
                <h2 className={styles.movementsTitle}>{t('admin.cashMovements.movements', 'Movimientos')}</h2>
                
                {loading ? (
                  <div className={styles.loading}>{t('common.loading', 'Cargando...')}</div>
                ) : cashMovements.length === 0 ? (
                  <div className={styles.emptyState}>
                    {t('admin.cashMovements.noMovements', 'No hay movimientos registrados para esta caja')}
                  </div>
                ) : (
                  <div className={styles.movementsList} ref={printRef}>
                    {filteredMovements.map((movement) => (
                      <div key={movement.id} className={styles.movementItem}>
                        <div className={`${styles.movementIcon} ${
                          movement.type === CashMovementType.ADDITION || movement.type === CashMovementType.INITIAL 
                            ? styles.additionIcon 
                            : movement.type === CashMovementType.WITHDRAWAL 
                              ? styles.withdrawalIcon 
                              : styles.closingIcon}`
                        }>
                          {movement.type === CashMovementType.ADDITION ? '📈' : 
                           movement.type === CashMovementType.WITHDRAWAL ? '📉' : 
                           movement.type === CashMovementType.INITIAL ? '💸' : '🔒'}
                        </div>
                        <div className={styles.movementInfo}>
                          <div className={styles.movementType}>
                            {getMovementTypeText(movement.type)}
                          </div>
                          {movement.notes && (
                            <div className={styles.movementNotes}>
                              {movement.notes}
                            </div>
                          )}
                        </div>
                        <div className={styles.movementTime}>
                          {formatDate(movement.date)}
                        </div>
                        <div className={`${styles.movementAmount} ${
                          movement.type === CashMovementType.ADDITION || movement.type === CashMovementType.INITIAL 
                            ? styles.addition 
                            : movement.type === CashMovementType.WITHDRAWAL 
                              ? styles.withdrawal 
                              : ''}`
                        }>
                          {movement.type === CashMovementType.WITHDRAWAL ? '-' : '+'}
                          {formatCurrency(movement.amount)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className={styles.emptyState}>
              {t('admin.cashMovements.selectRegister', 'Selecciona un registro de caja para ver sus movimientos')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CashMovementsAdmin;
